/**
 * Crops a screen-capture stream to an area, in a way that keeps running while the
 * AI Recording tab is in the background (the user is usually in the window being recorded).
 *
 * - Chrome/Edge: MediaStreamTrackProcessor → crop each VideoFrame → MediaStreamTrackGenerator.
 *   Driven by the capture's own frames (no requestAnimationFrame, which stops in background
 *   tabs). Cropping uses `visibleRect` (no pixel copy); if the browser rejects that for a
 *   frame format, it falls back to drawing the frame on an OffscreenCanvas.
 * - Other browsers: canvas + hidden <video>, redrawn by a Worker timer (Worker timers are not
 *   throttled like main-thread timers or paused like requestAnimationFrame).
 *
 * `area` is in stream pixels (PreviewAndCrop converts from preview CSS pixels).
 */
export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CropPipeline {
  /** Video-only stream of the cropped area. */
  stream: MediaStream;
  stop: () => void;
}

const FPS = 30;

/** Even numbers keep crops valid for 4:2:0 frame formats (NV12 / I420). */
const even = (n: number) => Math.max(2, Math.floor(n / 2) * 2);

/* eslint-disable @typescript-eslint/no-explicit-any */
const w = window as any;
const hasInsertableStreams =
  typeof w.MediaStreamTrackProcessor === 'function' &&
  typeof w.MediaStreamTrackGenerator === 'function' &&
  typeof w.VideoFrame === 'function';

export function createCropPipeline(source: MediaStream, area: CropArea): CropPipeline {
  const track = source.getVideoTracks()[0];
  if (!track) throw new Error('No video track to crop');
  return hasInsertableStreams ? frameCropPipeline(track, area) : canvasCropPipeline(source, area);
}

function frameCropPipeline(track: MediaStreamTrack, area: CropArea): CropPipeline {
  const outW = even(area.width);
  const outH = even(area.height);
  const processor = new w.MediaStreamTrackProcessor({ track });
  const generator = new w.MediaStreamTrackGenerator({ kind: 'video' });
  const abort = new AbortController();
  let frameErrorLogged = false;

  let useCanvas = false;
  let canvas: OffscreenCanvas | null = null;
  let ctx: OffscreenCanvasRenderingContext2D | null = null;

  const cropRect = (frame: any) => {
    // Map the area onto the frame's visible rect (normally identical to the display size).
    const vr = frame.visibleRect ?? { x: 0, y: 0, width: frame.displayWidth, height: frame.displayHeight };
    const sx = vr.width / (frame.displayWidth || vr.width);
    const sy = vr.height / (frame.displayHeight || vr.height);
    const x = Math.min(Math.floor((vr.x + area.x * sx) / 2) * 2, vr.x + vr.width - 2);
    const y = Math.min(Math.floor((vr.y + area.y * sy) / 2) * 2, vr.y + vr.height - 2);
    const width = Math.min(even(area.width * sx), vr.x + vr.width - x);
    const height = Math.min(even(area.height * sy), vr.y + vr.height - y);
    return { x, y, width: Math.max(2, Math.floor(width / 2) * 2), height: Math.max(2, Math.floor(height / 2) * 2) };
  };

  const transformer = new TransformStream({
    transform(frame: any, controller) {
      try {
        const rect = cropRect(frame);
        if (!useCanvas) {
          try {
            controller.enqueue(new w.VideoFrame(frame, { visibleRect: rect, displayWidth: outW, displayHeight: outH }));
            return;
          } catch (err) {
            console.warn('[cropPipeline] visibleRect crop not supported, using OffscreenCanvas:', err);
            useCanvas = true;
          }
        }
        if (!canvas) {
          canvas = new OffscreenCanvas(outW, outH);
          ctx = canvas.getContext('2d');
        }
        if (ctx) {
          ctx.drawImage(frame, rect.x, rect.y, rect.width, rect.height, 0, 0, outW, outH);
          controller.enqueue(new w.VideoFrame(canvas, { timestamp: frame.timestamp }));
        }
      } catch (err) {
        // Report once, not for every frame.
        if (!frameErrorLogged) {
          frameErrorLogged = true;
          console.error('[cropPipeline] frame dropped:', err);
        }
      } finally {
        frame.close();
      }
    },
  });

  processor.readable
    .pipeThrough(transformer, { signal: abort.signal })
    .pipeTo(generator.writable, { signal: abort.signal })
    .catch(() => { /* ends when the source ends or on stop() */ });

  return {
    stream: new MediaStream([generator]),
    stop: () => {
      abort.abort();
      try { generator.stop(); } catch { /* already stopped */ }
    },
  };
}

function canvasCropPipeline(source: MediaStream, area: CropArea): CropPipeline {
  const outW = even(area.width);
  const outH = even(area.height);
  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');

  // Hidden but in the DOM so autoplay policies (especially iOS) allow play().
  const video = document.createElement('video');
  video.srcObject = new MediaStream(source.getVideoTracks());
  video.muted = true;
  video.playsInline = true;
  video.setAttribute('playsinline', 'true');
  video.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;';
  document.body.appendChild(video);
  video.play().catch(() => { /* starts once data arrives */ });

  const draw = () => {
    if (video.readyState < 2) return;
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return;
    const sx = Math.max(0, Math.min(area.x, vw - 1));
    const sy = Math.max(0, Math.min(area.y, vh - 1));
    const sw = Math.max(1, Math.min(area.width, vw - sx));
    const sh = Math.max(1, Math.min(area.height, vh - sy));
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, outW, outH);
  };

  const tickerUrl = URL.createObjectURL(
    new Blob([`setInterval(() => postMessage(0), ${Math.round(1000 / FPS)});`], { type: 'text/javascript' }),
  );
  const ticker = new Worker(tickerUrl);
  ticker.onmessage = draw;

  const stream = canvas.captureStream(FPS);
  return {
    stream,
    stop: () => {
      ticker.terminate();
      URL.revokeObjectURL(tickerUrl);
      stream.getTracks().forEach((t) => t.stop());
      video.pause();
      video.srcObject = null;
      video.remove();
    },
  };
}
